/**
 * Scrollback search in a terminal pane (Terminal board card 2, 21.10.2026),
 * as in VS Code's terminal: Ctrl+F (⌘F on macOS) in a focused pane opens a
 * find bar over the pane; Enter / Shift+Enter step through the matches.
 * Pure helpers; the bar is TerminalSearchBar.js.
 */

/** True for the find chord: ⌘F on macOS, Ctrl+F elsewhere — no other modifier. */
export function isFindChord(e, isMac) {
  if (!e || e.type !== 'keydown' || e.altKey || e.shiftKey) return false;
  const mod = isMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  if (!mod) return false;
  // The letter F on the user's layout; the physical F key only where the
  // layout has no Latin letters there (Cyrillic, Greek…), as VS Code does.
  const k = String(e.key || '');
  return k.toLowerCase() === 'f' || (e.code === 'KeyF' && !/^[a-z]$/i.test(k));
}

/** Match highlights. xterm wants #RRGGBB; readable on the dark and light terminal. */
export const SEARCH_DECORATIONS = Object.freeze({
  matchBackground: '#6b5a1e',
  matchBorder: '#8a7426',
  matchOverviewRuler: '#d4a72c',
  activeMatchBackground: '#c28a00',
  activeMatchBorder: '#ffd24d',
  activeMatchColorOverviewRuler: '#ffd24d',
});

/** The addon's options for one search. `incremental` keeps the current match while typing. */
export const searchOptions = ({ caseSensitive = false, regex = false, wholeWord = false } = {}, incremental = false) => ({
  caseSensitive,
  regex,
  wholeWord,
  incremental,
  decorations: SEARCH_DECORATIONS,
});

/** "3 of 12", "No results", or '' for an empty query. A count past the highlight limit reads "1000+". */
export function resultLabel(query, { resultIndex = -1, resultCount = 0 } = {}, t = (k, d, o) => d.replace(/\{\{(\w+)\}\}/g, (_, n) => o[n])) {
  if (!query) return '';
  if (!resultCount) return t('terminalExt.searchNone', 'No results', {});
  const total = resultCount >= 1000 ? '1000+' : String(resultCount);
  if (resultIndex < 0) return t('terminalExt.searchCount', '{{total}} results', { total });
  return t('terminalExt.searchPosition', '{{index}} of {{total}}', { index: resultIndex + 1, total });
}
